/**
 * Firebase Service Initialization
 * PROTEGE QV Lecture+ — Project protegeqv-2532f
 */

import { initializeApp, getApps } from 'firebase/app';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getFirestore } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase only once
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

// Analytics
let analytics = null;
if (typeof window !== 'undefined' && import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
  try {
    analytics = getAnalytics(app);
  } catch {
    // Analytics unavailable (e.g. ad-blocker)
  }
}

// Firestore (used for chat rate limiting)
const db = getFirestore(app);

// Messaging (FCM)
let messaging = null;
if (typeof window !== 'undefined' && 'Notification' in window) {
  try {
    messaging = getMessaging(app);
  } catch {
    // Messaging unavailable in this browser
  }
}

/**
 * Track a custom analytics event
 * @param {string} eventName
 * @param {Record<string, unknown>} params
 */
export const trackEvent = (eventName, params = {}) => {
  if (analytics) {
    logEvent(analytics, eventName, params);
  }
};

/**
 * Log an error event to Analytics (web alternative to Crashlytics)
 * @param {Error} error
 * @param {string} context
 */
export const trackError = (error, context = 'unknown') => {
  trackEvent('app_error', {
    error_message: error?.message || String(error),
    error_context: context,
    error_stack: error?.stack?.substring(0, 200) || '',
  });
};

export { app, analytics, db, messaging, getToken, onMessage };
export default app;
